import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { lastValueFrom, map } from 'rxjs';

import { GroupedMarketState } from '../shared/models';

export interface GroupedMarketStateData {
	viewGroupedMarketsState: GroupedMarketState[];
}

/** Normalized GraphQL query result used by the app (Apollo Client 4-safe). */
export type GraphqlQueryResult<T> = { data: T };

/** Normalized GraphQL mutation result used by the app (Apollo Client 4-safe). */
export type GraphqlMutationResult<T> = { data?: T | null };

@Injectable({
	providedIn: 'root',
})
export class GraphqlService {
	constructor(private apollo: Apollo) {}

	public query<T>(query: string, variables?: any): Promise<GraphqlQueryResult<T>> {
		return lastValueFrom(
			this.apollo
				.query({
					query: gql(query),
					variables,
				})
				.pipe(map(result => ({ data: result.data as T }))),
		);
	}

	public mutate<T>(mutation: string, variables?: any): Promise<GraphqlMutationResult<T>> {
		return lastValueFrom(
			this.apollo
				.mutate({
					mutation: gql(mutation),
					variables,
				})
				.pipe(map(result => ({ data: (result.data as T | null | undefined) ?? null }))),
		);
	}

	public getGroupedMarketsState(
		marketType: 'commercial' | 'residential',
		pricingType: 'auction' | 'matrix' | 'both',
	): Promise<GraphqlQueryResult<GroupedMarketStateData>> {
		return lastValueFrom(
			this.apollo
				.query({
					query: gql`
						query ViewGroupedMarketsState($criteria: ViewGroupedMarketInput) {
							viewGroupedMarketsState(criteria: $criteria) {
								stateId
								stateLong
								stateShort
								suppliers {
									supplierId
									name
									hasElectricity
									hasNaturalGas
								}
							}
						}
					`,
					variables: {
						criteria: { marketType, pricingType },
					},
				})
				.pipe(map(result => ({ data: result.data as GroupedMarketStateData }))),
		);
	}
}
